import React from "react";
import {
  Radar,
  RadarChart as ReRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width:${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    setIsMobile(mq.matches); // sync on mount
    return () => mq.removeEventListener?.("change", onChange);
  }, [breakpoint]);
  return isMobile;
}

export default function RadarChart({ data, minDomain = 0, maxDomain }) {
  const isMobile = useIsMobile();
  if (!data || !data.length) return <div>No data</div>;

  const scores = data.map((d) => Number(d.score) || 0);
  const computedMax =
    maxDomain ?? Math.ceil((Math.max(...scores) || 0) / 5) * 5 + 5; // nice rim
  const domainMin = Number(minDomain) || 0;
  const domainMax = Math.max(domainMin + 1, computedMax);

  const scoreByMetric = Object.fromEntries(
    data.map((d) => [String(d.metric), Number(d.score) || 0])
  );

  // Mobile-only layout to avoid clipping/overlap
  const mobileCfg = {
    height: 360,
    margin: { top: 34, right: 64, bottom: 44, left: 64 }, // extra side room
    outerRadius: "62%",
    labelFont: 10,
    valueFont: 9,
    labelFactor: 1.02,   // label slightly inside/outside rim
    valueInward: 10,     // pull vertex numbers inward along the ray
  };

  const AngleTick = (props) => {
    const { x, y, cx, cy, payload, textAnchor } = props;
    const metric = String(payload?.value ?? "");
    const score = scoreByMetric[metric];
    if (score == null) return null;

    // Vector from center -> rim tick
    const dx = x - cx;
    const dy = y - cy;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;

    // Data vertex position (same as polygon corner)
    const t = Math.max(
      0,
      Math.min(1, (score - domainMin) / (domainMax - domainMin))
    );
    const vx = cx + dx * t;
    const vy = cy + dy * t;

    // Position the vertex value:
    // - desktop: nudge OUTWARD a bit (your original)
    // - mobile : nudge INWARD so it doesn't collide with the label
    const valueNudge = isMobile ? -mobileCfg.valueInward : 6;
    const valueX = vx + ux * valueNudge;
    const valueY = vy + uy * valueNudge;

    // Metric label (mobile: slightly closer to rim to keep inside SVG)
    const labelFactor = isMobile ? mobileCfg.labelFactor : 1.10;
    const labelX = cx + dx * labelFactor;
    const labelY = cy + dy * labelFactor;

    return (
      <g>
        {/* vertex value */}
        <text
          x={valueX}
          y={valueY}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#0f172a"
          fontWeight={700}
          fontSize={isMobile ? mobileCfg.valueFont : 12}
        >
          {Math.round(score)}
        </text>

        {/* metric label */}
        <text
          x={labelX}
          y={labelY}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fill="#64748b"
          fontSize={isMobile ? mobileCfg.labelFont : 12}
        >
          {metric}
        </text>
      </g>
    );
  };

  return (
    <div style={{ width: "100%", height: isMobile ? mobileCfg.height : 400 }}>
      <ResponsiveContainer>
        <ReRadarChart
          data={data}
          margin={isMobile ? mobileCfg.margin : undefined}
          outerRadius={isMobile ? mobileCfg.outerRadius : undefined}
        >
       <PolarGrid />

{/* Draw the shape first so labels render on top */}
<Radar
  name="Average Score"
  dataKey="score"
  stroke="#6366F1"
  fill="#6366F1"
  fillOpacity={0.6}
  dot={{ r: 2 }}
/>

<PolarAngleAxis
  dataKey="metric"
  tickLine={false}
  axisLine={false}
  tick={AngleTick}
/>

<PolarRadiusAxis domain={[domainMin, domainMax]} tick={false}  axisLine={false}  />

        </ReRadarChart>
      </ResponsiveContainer>
    </div>
  );
}
